import { useEffect, useState } from "react";
import { Wordmark } from "./brand";

// The left half of the sign-in screen: the desk, running, behind glass.
//
// Everything on it is real. The tape carries the same reference prices the
// pricing engine quotes against, and the figures are the live book — which is
// the point. A screenshot of a dashboard would be marketing; the actual desk
// trading while you sign in is the product demonstrating itself.
//
// It is also the only unauthenticated surface, so it shows aggregate totals
// and reference prices and nothing else: no counterparty names, no order
// detail, nothing that identifies who trades here.

interface PublicOverview {
  marks: { asset: string; price: number; change_24h: number | null }[];
  /** null unless PUBLIC_DESK_STATS is on, which is NOT the default. */
  totals: { volume_eur_30d: number; trades_30d: number } | null;
  reconciled: { matched: number; compared: number } | null;
}

const LABEL: Record<string, string> = {
  BTC: "BTC/EUR", ETH: "ETH/EUR", SOL: "SOL/EUR", USDT_ERC20: "USDT/EUR",
};

function price(n: number): string {
  const dp = n >= 1 ? 2 : 4;
  return n.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

function compact(n: number): string {
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return n.toFixed(0);
}

export function AuthStage() {
  const [d, setD] = useState<PublicOverview | null>(null);

  useEffect(() => {
    // Public endpoint, no session. Failure is silent and the stage simply
    // renders without figures — a sign-in screen must never depend on an
    // unauthenticated fetch succeeding.
    fetch("/api/overview/public")
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => j && setD(j))
      .catch(() => undefined);
  }, []);

  return (
    <div className="auth-stage">
      {d && d.marks.length > 0 && (
        <div className="tape" style={{ position: "absolute", top: 0, left: 0, right: 0, background: "transparent" }}>
          {d.marks.map((m) => (
            <div className="tape-item" key={m.asset}>
              <span className="a">{LABEL[m.asset] ?? m.asset}</span>
              <span className="p">{price(m.price)}</span>
              {m.change_24h !== null && (
                <span className={m.change_24h >= 0 ? "gain" : "loss"}>
                  {m.change_24h >= 0 ? "+" : "-"}{Math.abs(m.change_24h).toFixed(2)}%
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="brand"><Wordmark height={19} /></div>

      <div className="auth-lede">
        <h2>The desk runs itself.<br />You watch the book.</h2>
        <p>
          Orders priced, routed across five liquidity venues, executed and reconciled. No trader
          touches them.
        </p>
        {d?.totals && (
          <div className="auth-figures">
            <div>
              <div className="v num">{compact(d.totals.volume_eur_30d)}</div>
              <div className="l">traded, 30 days</div>
            </div>
            <div>
              <div className="v num gain">{d.totals.trades_30d.toLocaleString("en-US")}</div>
              <div className="l">fills, zero manual</div>
            </div>
            {d?.reconciled && (
              <div>
                <div className="v num">{d.reconciled.matched}/{d.reconciled.compared}</div>
                <div className="l">positions reconciled</div>
              </div>
            )}
          </div>
        )}
      </div>

      <svg className="auth-curve" viewBox="0 0 900 190" preserveAspectRatio="none" aria-hidden>
        <defs>
          <linearGradient id="authFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.2" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M0,160 L60,152 L120,156 L180,140 L240,150 L300,132 L360,142 L420,124 L480,134 L540,112 L600,124 L660,98 L720,88 L780,44 L840,26 L900,14 L900,190 L0,190 Z" fill="url(#authFill)" />
        <path d="M0,160 L60,152 L120,156 L180,140 L240,150 L300,132 L360,142 L420,124 L480,134 L540,112 L600,124 L660,98 L720,88 L780,44 L840,26 L900,14"
              fill="none" stroke="var(--accent)" strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}
